import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';
import { useToast } from '../context/ToastContext';
import { useConfirm } from '../context/ConfirmContext';
import { fmtIstDateTime } from '../utils/dateUtils';
import { useBrandstoreAccess } from './brandstoreAccess';

// Admin screens for reseller partners (e.g. ZikZuk) that call the partner API
// (/api/partner/v1) from their own backend: the returns queue ops works through, and
// the API keys a partner authenticates with. Backend: BrandstoreAdminController.

// ── Shared styles (mirrors BrandstoreManagement's local style consts) ──────
const card  = { background: '#fff', border: '1.5px solid #E5E7EB', borderRadius: 12, padding: '20px 24px', marginBottom: 16 };
const label = { display: 'block', fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 5 };
const input = { width: '100%', padding: '9px 11px', border: '1.5px solid #E5E7EB', borderRadius: 7, fontSize: 14, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box', color: '#111827' };
const btnPrimary = { padding: '9px 20px', background: 'var(--color-primary,#F29213)', color: '#fff', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 700, cursor: 'pointer' };
const btnGhost  = { padding: '8px 16px', background: '#fff', color: '#4B5563', border: '1.5px solid #E5E7EB', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const th = { padding: '11px 13px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#9CA3AF', letterSpacing: '0.5px', whiteSpace: 'nowrap' };
const td = { padding: '10px 13px', fontSize: 12, color: '#374151', verticalAlign: 'top' };

const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;

const RETURN_STATUSES = ['REQUESTED', 'APPROVED', 'PICKED_UP', 'RECEIVED', 'REJECTED'];
const RETURN_STATUS_LABEL = {
  REQUESTED: 'Requested', APPROVED: 'Approved', PICKED_UP: 'Picked up', RECEIVED: 'Received', REJECTED: 'Rejected',
};
const RETURN_STATUS_COLORS = {
  REQUESTED: { bg: '#FFF8EC', color: '#D97D0A' },
  APPROVED:  { bg: '#EFF6FF', color: '#1D4ED8' },
  PICKED_UP: { bg: '#DCFCE7', color: '#15803D' },
  RECEIVED:  { bg: '#EDE7F6', color: '#4527A0' },
  REJECTED:  { bg: '#FEE2E2', color: '#B91C1C' },
};
// Mirrors PartnerOrderService.isAllowedTransition — the server enforces it; this only
// decides which buttons to offer.
const NEXT_ACTIONS = {
  REQUESTED: ['APPROVED', 'REJECTED'],
  APPROVED:  ['PICKED_UP', 'RECEIVED', 'REJECTED'],
  PICKED_UP: ['RECEIVED'],
  RECEIVED:  [],
  REJECTED:  [],
};
const ACTION_LABEL = { APPROVED: 'Approve', PICKED_UP: 'Mark picked up', RECEIVED: 'Mark received', REJECTED: 'Reject' };

function StatusPill({ status }) {
  const c = RETURN_STATUS_COLORS[status] || { bg: '#f5f5f5', color: '#555' };
  return (
    <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: c.bg, color: c.color, whiteSpace: 'nowrap', display: 'inline-block' }}>
      {RETURN_STATUS_LABEL[status] || status}
    </span>
  );
}

function Pager({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginTop: 14 }}>
      <button onClick={() => onChange(Math.max(0, page - 1))} disabled={page === 0} style={{ ...btnGhost, padding: '6px 14px', fontSize: 12, opacity: page === 0 ? 0.5 : 1 }}>← Prev</button>
      <span style={{ fontSize: 12, color: '#9CA3AF' }}>Page {page + 1} of {totalPages}</span>
      <button onClick={() => onChange(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1} style={{ ...btnGhost, padding: '6px 14px', fontSize: 12, opacity: page >= totalPages - 1 ? 0.5 : 1 }}>Next →</button>
    </div>
  );
}

function Modal({ title, onClose, children, width = 640 }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div role="dialog" aria-modal="true" aria-label={title} onClick={e => e.stopPropagation()}
           style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: width, maxHeight: '90vh', overflowY: 'auto', padding: '22px 26px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: '#111827', margin: 0 }}>{title}</h3>
          <button onClick={onClose} aria-label="Close" style={{ ...btnGhost, padding: '2px 10px', fontSize: 16 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── Return detail / actions ────────────────────────────────────────────────

function ReturnDetailModal({ returnId, onClose, onChanged }) {
  const { canProcessReturns } = useBrandstoreAccess();
  const { addToast } = useToast();
  const confirm = useConfirm();
  const [ret, setRet]         = useState(null);
  const [note, setNote]       = useState('');
  const [restock, setRestock] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  useEffect(() => {
    api.get(`/admin/brandstore/returns/${returnId}`)
      .then(r => setRet(r.data))
      .catch(() => setError('Could not load this return.'));
  }, [returnId]);

  const act = async (status) => {
    setError('');
    if (status === 'REJECTED' && !note.trim()) {
      setError('Add a note explaining the rejection. The partner sees it.');
      return;
    }
    if (status === 'RECEIVED') {
      const ok = await confirm({
        title: 'Mark this return as received?',
        message: `The credit is fixed now and deducted from ${ret.tenantName || 'the partner'}'s next invoice. `
          + (restock ? 'The units go back into sellable stock.' : 'The units will NOT go back into stock.'),
        confirmLabel: 'Mark received',
      });
      if (!ok) return;
    }
    setSaving(true);
    try {
      const res = await api.put(`/admin/brandstore/returns/${returnId}/status`, {
        status, note: note.trim() || undefined, restock: status === 'RECEIVED' ? restock : undefined,
      });
      setRet(res.data);
      setNote('');
      addToast(`Return ${res.data.returnRef} is now ${RETURN_STATUS_LABEL[res.data.status].toLowerCase()}.`, 'success');
      onChanged();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not update this return.');
    } finally {
      setSaving(false);
    }
  };

  const actions = ret ? NEXT_ACTIONS[ret.status] || [] : [];

  return (
    <Modal title={ret ? `Return ${ret.returnRef}` : 'Return'} onClose={onClose}>
      {!ret ? (
        <div style={{ padding: 30, textAlign: 'center', color: error ? '#B91C1C' : '#9CA3AF', fontSize: 13 }}>{error || 'Loading…'}</div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 16 }}>
            <div><span style={label}>Company</span><span style={{ fontSize: 13, color: '#111827' }}>{ret.tenantName}</span></div>
            <div><span style={label}>Partner order</span><span style={{ fontSize: 13, color: '#111827' }}>{ret.externalRef}</span></div>
            <div><span style={label}>Status</span><StatusPill status={ret.status} /></div>
            <div><span style={label}>Requested</span><span style={{ fontSize: 13, color: '#111827' }}>{fmtIstDateTime(ret.createdAt)}</span></div>
          </div>

          <span style={label}>Reason given by partner</span>
          <div style={{ fontSize: 13, color: '#111827', background: '#F9FAFB', borderRadius: 7, padding: '9px 12px', marginBottom: 16, whiteSpace: 'pre-wrap' }}>{ret.reason}</div>

          <span style={label}>Items</span>
          <div style={{ border: '1.5px solid #E5E7EB', borderRadius: 8, overflow: 'hidden', marginBottom: 16 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#F9FAFB' }}>
                  {['Product', 'SKU', 'Returning', 'Credit', 'Restocked'].map(h => <th key={h} style={th}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {ret.items.map(i => (
                  <tr key={i.orderId} style={{ borderTop: '1px solid #F3F4F6' }}>
                    <td style={td}>{i.productName}</td>
                    <td style={td}>{i.sku || '—'}</td>
                    <td style={td}>{i.quantity} of {i.orderedQuantity}</td>
                    <td style={td}>{i.creditAmountInr != null ? inr(i.creditAmountInr) : '—'}</td>
                    <td style={td}>{i.restocked == null ? '—' : i.restocked ? 'Yes' : 'No'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {ret.pickupAddress && (
            <>
              <span style={label}>Pickup from</span>
              <div style={{ fontSize: 13, color: '#111827', marginBottom: 16, lineHeight: 1.5 }}>
                {ret.pickupAddress.name} · {ret.pickupAddress.phone}<br />
                {[ret.pickupAddress.addressLine1, ret.pickupAddress.addressLine2].filter(Boolean).join(', ')}<br />
                {ret.pickupAddress.city}, {ret.pickupAddress.state} {ret.pickupAddress.pincode}
              </div>
            </>
          )}

          {ret.creditAmountInr != null && (
            <div style={{ background: 'var(--color-primary-subtle,#FFF3E0)', borderRadius: 8, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <span style={{ fontSize: 13, color: '#374151' }}>
                Credit to partner{ret.invoiceId ? ' (deducted on an invoice)' : ' (deducted on the next invoice)'}
              </span>
              <strong style={{ fontSize: 16, color: '#111827' }}>{inr(ret.creditAmountInr)}</strong>
            </div>
          )}

          {ret.resolutionNote && (
            <>
              <span style={label}>Note to partner</span>
              <div style={{ fontSize: 13, color: '#111827', marginBottom: 16, whiteSpace: 'pre-wrap' }}>{ret.resolutionNote}</div>
            </>
          )}

          {canProcessReturns && actions.length > 0 && (
            <div style={{ borderTop: '1.5px solid #F3F4F6', paddingTop: 16 }}>
              <label style={label} htmlFor="return-note">Note to partner (required to reject; e.g. pickup date)</label>
              <textarea id="return-note" style={{ ...input, minHeight: 64, resize: 'vertical', marginBottom: 10 }}
                        value={note} onChange={e => setNote(e.target.value)} maxLength={1000} />
              {actions.includes('RECEIVED') && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#374151', marginBottom: 12 }}>
                  <input type="checkbox" checked={restock} onChange={e => setRestock(e.target.checked)} />
                  Put the units back into sellable stock when received (untick for damaged items)
                </label>
              )}
              {error && <div style={{ color: '#B91C1C', fontSize: 13, marginBottom: 10 }}>{error}</div>}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {actions.map(a => (
                  <button key={a} disabled={saving} onClick={() => act(a)}
                          style={a === 'REJECTED'
                            ? { ...btnGhost, color: '#B91C1C', borderColor: '#FCA5A5', opacity: saving ? 0.5 : 1 }
                            : { ...btnPrimary, opacity: saving ? 0.5 : 1 }}>
                    {ACTION_LABEL[a]}
                  </button>
                ))}
              </div>
            </div>
          )}
          {actions.length === 0 && error && <div style={{ color: '#B91C1C', fontSize: 13 }}>{error}</div>}
        </>
      )}
    </Modal>
  );
}

// ── Returns tab ────────────────────────────────────────────────────────────

export function PartnerReturnsTab({ tenants }) {
  const [tenantId, setTenantId]     = useState('');
  const [status, setStatus]         = useState('REQUESTED');
  const [returns, setReturns]       = useState([]);
  const [page, setPage]             = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [viewId, setViewId]         = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    api.get('/admin/brandstore/returns', { params: { tenantId: tenantId || undefined, status: status || undefined, page, size: 20 } })
      .then(r => { setReturns(r.data.content || []); setTotalPages(r.data.totalPages || 0); })
      .catch(() => setError('Could not load returns.'))
      .finally(() => setLoading(false));
  }, [tenantId, status, page]);

  useEffect(() => { setPage(0); }, [tenantId, status]);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: 0 }}>Partner Returns</h2>
        <p style={{ fontSize: 13, color: '#9CA3AF', margin: '4px 0 0' }}>
          Returns requested by reseller partners through the API. eYantra collects the item; once it's received, the credit is deducted from the partner's next weekly invoice.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap', alignItems: 'center' }}>
        <select aria-label="Company" value={tenantId} onChange={e => setTenantId(e.target.value)} style={{ ...input, width: 'auto', padding: '8px 10px' }}>
          <option value="">All companies</option>
          {tenants.map(t => <option key={t.id} value={t.id}>{t.displayName}</option>)}
        </select>
        <select aria-label="Status" value={status} onChange={e => setStatus(e.target.value)} style={{ ...input, width: 'auto', padding: '8px 10px' }}>
          <option value="">All statuses</option>
          {RETURN_STATUSES.map(s => <option key={s} value={s}>{RETURN_STATUS_LABEL[s]}</option>)}
        </select>
        <button onClick={load} style={btnGhost}>↻ Refresh</button>
      </div>

      {error ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#B91C1C' }}>{error}</div>
      ) : loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>Loading…</div>
      ) : returns.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>
          {status ? `No ${RETURN_STATUS_LABEL[status].toLowerCase()} returns.` : 'No returns yet.'}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <div style={{ background: '#fff', borderRadius: 12, border: '1.5px solid #E5E7EB', overflow: 'hidden', minWidth: 760 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#F9FAFB', borderBottom: '1.5px solid #E5E7EB' }}>
                  {['Return', 'Company', 'Partner order', 'Items', 'Reason', 'Status', 'Requested', ''].map(h => <th key={h} style={th}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {returns.map((r, idx) => (
                  <tr key={r.id} style={{ borderBottom: '1px solid #F3F4F6', background: idx % 2 === 0 ? '#fff' : '#FAFAFA' }}>
                    <td style={{ ...td, fontWeight: 700, color: 'var(--color-primary,#F29213)' }}>{r.returnRef}</td>
                    <td style={td}>{r.tenantName}</td>
                    <td style={td}>{r.externalRef}</td>
                    <td style={td}>{r.items.map(i => `${i.quantity}× ${i.productName}`).join(', ')}</td>
                    <td style={{ ...td, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.reason}>{r.reason}</td>
                    <td style={td}><StatusPill status={r.status} /></td>
                    <td style={{ ...td, whiteSpace: 'nowrap', color: '#9CA3AF' }}>{fmtIstDateTime(r.createdAt)}</td>
                    <td style={td}>
                      <button onClick={() => setViewId(r.id)} style={{ ...btnGhost, padding: '4px 10px', fontSize: 11 }}>
                        {(NEXT_ACTIONS[r.status] || []).length ? 'Review' : 'View'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager page={page} totalPages={totalPages} onChange={setPage} />
        </div>
      )}

      {viewId && <ReturnDetailModal returnId={viewId} onClose={() => setViewId(null)} onChanged={load} />}
    </div>
  );
}

// ── API keys tab (ADMIN only — the backend refuses everyone else) ──────────

function NewKeyModal({ issued, onClose }) {
  const { addToast } = useToast();
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(issued.apiKey);
      addToast('API key copied.', 'success');
    } catch {
      addToast('Could not copy. Select the key and copy it manually.', 'error');
    }
  };
  return (
    <Modal title="API key created" onClose={onClose} width={560}>
      <div style={{ background: '#FEF3C7', color: '#92400E', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 14 }}>
        Copy this key now and send it to the partner securely. It won't be shown again; if it's lost, revoke it and create a new one.
      </div>
      <label style={label} htmlFor="new-api-key">X-Api-Key</label>
      <input id="new-api-key" readOnly value={issued.apiKey} onFocus={e => e.target.select()}
             style={{ ...input, fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: 13, marginBottom: 14 }} />
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button style={btnGhost} onClick={copy}>Copy key</button>
        <button style={btnPrimary} onClick={onClose}>Done</button>
      </div>
    </Modal>
  );
}

function AllowedIpsModal({ apiKey, tenantId, onClose, onSaved }) {
  const { addToast } = useToast();
  const [value, setValue] = useState(apiKey.allowedIpRanges || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await api.put(`/admin/brandstore/tenants/${tenantId}/partner-keys/${apiKey.id}/allowed-ips`, { allowedIpRanges: value });
      addToast('IP allowlist saved.', 'success');
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save the IP allowlist.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={`IP allowlist: ${apiKey.label || apiKey.keyId}`} onClose={onClose} width={560}>
      <p style={{ fontSize: 13, color: '#6B7280', margin: '0 0 12px' }}>
        The partner's server IPs or CIDR ranges, separated by commas (e.g. <code>203.0.113.10, 198.51.100.0/24</code>).
        Calls from any other IP are refused even with the right key. Leave empty to allow any IP (not recommended in production).
      </p>
      <label style={label} htmlFor="allowed-ips">Allowed IPs</label>
      <textarea id="allowed-ips" style={{ ...input, minHeight: 70, resize: 'vertical', fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: 13, marginBottom: 10 }}
                value={value} onChange={e => setValue(e.target.value)} maxLength={1000} />
      {error && <div style={{ color: '#B91C1C', fontSize: 13, marginBottom: 10 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button style={btnGhost} onClick={onClose}>Cancel</button>
        <button style={{ ...btnPrimary, opacity: saving ? 0.5 : 1 }} disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</button>
      </div>
    </Modal>
  );
}

export function PartnerApiKeysTab({ tenants }) {
  const { addToast } = useToast();
  const confirm = useConfirm();
  const [tenantId, setTenantId] = useState('');
  const [keys, setKeys]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newIps, setNewIps]     = useState('');
  const [createError, setCreateError] = useState('');
  const [creating, setCreating] = useState(false);
  const [issued, setIssued]     = useState(null);
  const [editIps, setEditIps]   = useState(null);

  const tenant = tenants.find(t => String(t.id) === String(tenantId));

  const load = useCallback(() => {
    if (!tenantId) { setKeys([]); return; }
    setLoading(true);
    setError('');
    api.get(`/admin/brandstore/tenants/${tenantId}/partner-keys`)
      .then(r => setKeys(r.data || []))
      .catch(err => setError(err.response?.status === 403 ? 'Only admins can manage partner API keys.' : 'Could not load API keys.'))
      .finally(() => setLoading(false));
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setCreating(true);
    try {
      setCreateError('');
      const res = await api.post(`/admin/brandstore/tenants/${tenantId}/partner-keys`, {
        label: newLabel.trim() || undefined,
        allowedIpRanges: newIps.trim() || undefined,
      });
      setIssued(res.data);
      setNewLabel('');
      setNewIps('');
      load();
    } catch (err) {
      setCreateError(err.response?.data?.error || 'Could not create the API key.');
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (k) => {
    const ok = await confirm({
      title: `Revoke ${k.label || k.keyId}?`,
      message: `${tenant?.displayName || 'The partner'}'s system stops working with this key immediately. This can't be undone.`,
      confirmLabel: 'Revoke key',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/brandstore/tenants/${tenantId}/partner-keys/${k.id}`);
      addToast('API key revoked.', 'success');
      load();
    } catch (err) {
      addToast(err.response?.data?.error || 'Could not revoke the API key.', 'error');
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: 0 }}>Partner API Access</h2>
        <p style={{ fontSize: 13, color: '#9CA3AF', margin: '4px 0 0' }}>
          API keys let a reseller partner's backend read this company's catalog and place orders that are billed to it weekly. Treat a key like a password.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap', alignItems: 'center' }}>
        <select aria-label="Company" value={tenantId} onChange={e => setTenantId(e.target.value)} style={{ ...input, width: 'auto', padding: '8px 10px' }}>
          <option value="">Select company…</option>
          {tenants.map(t => <option key={t.id} value={t.id}>{t.displayName}</option>)}
        </select>
      </div>

      {!tenantId ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#9CA3AF' }}>Select a company to manage its API keys.</div>
      ) : error ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#B91C1C' }}>{error}</div>
      ) : (
        <>
          <div style={card}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 220px' }}>
                <label style={label} htmlFor="key-label">Label (who or what uses it)</label>
                <input id="key-label" style={input} placeholder="e.g. ZikZuk production" value={newLabel}
                       onChange={e => setNewLabel(e.target.value)} maxLength={120} />
              </div>
              <div style={{ flex: '2 1 300px' }}>
                <label style={label} htmlFor="key-ips">Allowed IPs (partner's server IPs / CIDRs, comma-separated)</label>
                <input id="key-ips" style={{ ...input, fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: 13 }}
                       placeholder="e.g. 203.0.113.10, 198.51.100.0/24 (empty = any IP)" value={newIps}
                       onChange={e => setNewIps(e.target.value)} maxLength={1000} />
              </div>
              <button style={{ ...btnPrimary, opacity: creating ? 0.5 : 1 }} disabled={creating} onClick={create}>
                {creating ? 'Creating…' : '+ Create API key'}
              </button>
            </div>
            {createError && <div style={{ color: '#B91C1C', fontSize: 13, marginTop: 10 }}>{createError}</div>}
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>Loading…</div>
          ) : keys.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>No API keys for {tenant?.displayName} yet.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <div style={{ background: '#fff', borderRadius: 12, border: '1.5px solid #E5E7EB', overflow: 'hidden', minWidth: 860 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#F9FAFB', borderBottom: '1.5px solid #E5E7EB' }}>
                      {['Label', 'Key ID', 'Allowed IPs', 'Created', 'Last used', 'Status', ''].map(h => <th key={h} style={th}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {keys.map((k, idx) => (
                      <tr key={k.id} style={{ borderBottom: '1px solid #F3F4F6', background: idx % 2 === 0 ? '#fff' : '#FAFAFA' }}>
                        <td style={{ ...td, fontWeight: 600, color: '#111827' }}>{k.label || '—'}</td>
                        <td style={{ ...td, fontFamily: 'ui-monospace, Menlo, Consolas, monospace' }}>{k.keyId}</td>
                        <td style={{ ...td, maxWidth: 220 }}>
                          {k.allowedIpRanges
                            ? <span style={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace', wordBreak: 'break-all' }}>{k.allowedIpRanges}</span>
                            : <span title="This key works from any IP address" style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: '#FEF3C7', color: '#B45309' }}>Any IP</span>}
                        </td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtIstDateTime(k.createdAt)}{k.createdBy ? <div style={{ color: '#9CA3AF' }}>{k.createdBy}</div> : null}</td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{k.lastUsedAt ? fmtIstDateTime(k.lastUsedAt) : 'Never'}</td>
                        <td style={td}>
                          {k.revokedAt
                            ? <span title={`Revoked by ${k.revokedBy || 'unknown'}`} style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: '#F3F4F6', color: '#6B7280' }}>Revoked {fmtIstDateTime(k.revokedAt)}</span>
                            : <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: '#DCFCE7', color: '#15803D' }}>Active</span>}
                        </td>
                        <td style={td}>
                          {!k.revokedAt && (
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button onClick={() => setEditIps(k)} style={{ ...btnGhost, padding: '4px 10px', fontSize: 11 }}>Edit IPs</button>
                              <button onClick={() => revoke(k)} style={{ ...btnGhost, padding: '4px 10px', fontSize: 11, color: '#B91C1C', borderColor: '#FCA5A5' }}>Revoke</button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {issued && <NewKeyModal issued={issued} onClose={() => setIssued(null)} />}
      {editIps && (
        <AllowedIpsModal apiKey={editIps} tenantId={tenantId} onClose={() => setEditIps(null)}
                         onSaved={() => { setEditIps(null); load(); }} />
      )}
    </div>
  );
}
